// General athlete-documents panel (backed by /api/athlete-files). One panel per
// visibility tier: 'shared' (athlete + guardians + staff) or 'admin_only'
// (staff only). Pass asAthleteId when an admin acts on a specific athlete.
// variant: 'card' (default, athlete /profile) or 'group' (compact box used
// two-up on the concierge page, ConciergePage.css .cn-doc*).
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const fmtSize = (b) => {
  if (b === null || b === undefined) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

// "6/14/26"
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + 'Z');
  if (isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${String(d.getDate()).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;
};

const linkBtn = { background: 'none', border: 'none', padding: 0, fontSize: 13, fontWeight: 600, color: '#4D8064', cursor: 'pointer', flexShrink: 0 };
const ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png';

export default function DocumentsPanel({ visibility = 'shared', asAthleteId = null, title = 'Documents', hint, variant = 'card', internal = false, onSaved }) {
  const { API_URL } = useAuth();
  const fileRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const params = asAthleteId ? { as_athlete_id: asAthleteId } : undefined;
  const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, ...(params ? { params } : {}) });

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/athlete-files`, cfg())
      .then(res => setDocs((res.data || []).filter(d => d.visibility === visibility)))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load documents.'))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line
  useEffect(load, [API_URL, asAthleteId, visibility]);

  const onUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setError('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('label', file.name);
    fd.append('visibility', visibility);
    axios.post(`${API_URL}/athlete-files/upload`, fd, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' },
      ...(params ? { params } : {}),
    })
      .then(() => { load(); if (onSaved) onSaved(); })
      .catch(err => setError(err.response?.data?.detail || 'Upload failed.'))
      .finally(() => { setUploading(false); if (fileRef.current) fileRef.current.value = ''; });
  };

  const onDownload = (doc) => {
    const qs = params ? `?as_athlete_id=${asAthleteId}` : '';
    fetch(`${API_URL}/athlete-files/${doc.id}/download${qs}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = doc.filename; a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => setError('Download failed.'));
  };

  const onDelete = (doc) => {
    axios.delete(`${API_URL}/athlete-files/${doc.id}`, cfg())
      .then(() => { setDocs(prev => prev.filter(d => d.id !== doc.id)); if (onSaved) onSaved(); })
      .catch(err => setError(err.response?.data?.detail || 'Delete failed.'));
  };

  // ---- Compact group variant (concierge) ----
  if (variant === 'group') {
    return (
      <div className={`cn-docgroup${internal ? ' cn-docgroup--internal' : ''}`}>
        <div className="cn-docgroup__head">
          <span className={`cn-docgroup__title cn-docgroup__title--${internal ? 'internal' : 'shared'}`}>{title}</span>
          {hint && <span className="cn-docgroup__hint">{hint}</span>}
          <label className="cn-docgroup__upload">
            {uploading ? 'Uploading…' : 'Upload'}
            <input ref={fileRef} type="file" accept={ACCEPT} hidden onChange={onUpload} disabled={uploading} />
          </label>
        </div>
        {error && <div style={{ marginBottom: 6 }}><span className="cn-inline-msg cn-inline-msg--err">{error}</span></div>}
        {loading ? (
          <div className="cn-docgroup__empty">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="cn-docgroup__empty">No documents.</div>
        ) : (
          docs.map(d => (
            <div className="cn-docrow" key={d.id}>
              <span className="cn-docrow__name" title={d.label || d.filename} onClick={() => onDownload(d)} style={{ cursor: 'pointer' }}>{d.label || d.filename}</span>
              <span className="cn-docrow__meta">{[d.uploaded_by_name, fmtDate(d.uploaded_at)].filter(Boolean).join(' · ')}</span>
              <button type="button" className="cn-docrow__del" title="Delete" onClick={() => onDelete(d)}>✕</button>
            </div>
          ))
        )}
      </div>
    );
  }

  // ---- Full card variant (athlete /profile) ----
  return (
    <div className="card" style={{ marginBottom: 16, padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{title}</h2>
        <label className="pz-btn pz-btn--secondary pz-btn--sm" style={{ cursor: 'pointer', fontSize: 12, padding: '6px 12px', flexShrink: 0 }}>
          {uploading ? 'Uploading…' : '+ Upload'}
          <input ref={fileRef} type="file" accept={ACCEPT} hidden onChange={onUpload} disabled={uploading} />
        </label>
      </div>
      {hint && <div style={{ color: '#64748b', fontSize: 13, marginBottom: 12 }}>{hint}</div>}
      {error && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 8 }}>{error}</div>}

      {loading ? (
        <div style={{ color: '#64748b', fontSize: 14 }}>Loading…</div>
      ) : docs.length === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 14 }}>No documents.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {docs.map(d => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8 }}>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14, color: '#0f172a' }}>{d.label || d.filename}</span>
              <span style={{ fontSize: 12, color: '#94a3b8', flexShrink: 0 }}>{fmtSize(d.file_size)}</span>
              <button type="button" onClick={() => onDownload(d)} style={linkBtn}>Download</button>
              <button type="button" onClick={() => onDelete(d)} style={{ ...linkBtn, color: '#b91c1c' }}>Delete</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
